/**
 * This custom hook provides a flexible functionality to sign-in via Moodle.
 * It automatically handles the process to sign-in the user and initializes roles.
 */

import {useContext, useEffect, useState} from "react";
import {firebaseService} from "../../firebase/Firebase";
import {FirebaseAuthContext} from "../../firebase/FirebaseAuthProvider";
import {useIntl} from "react-intl";
import {useSnackbar} from "notistack";
import {useHistory} from "react-router-dom";

const useMoodleSignIn = (firebaseCustomToken, ltiKey, moodleEmail, moodleName, moodleReturnUrl) => {

    const {setSignedInUserMoodleLtiKey} = useContext(FirebaseAuthContext)

    const {enqueueSnackbar} = useSnackbar();

    // Internationalization for simple strings
    const intl = useIntl();

    const {clearUserData} = useContext(FirebaseAuthContext)
    const history = useHistory();

    /*
     Is only set to true if the authentication actively triggered by the user is in progress.
     This is only the case when the user actively triggers a login action.
     This is necessary in order to prevent an too early redirection in the related UI (SignInSectionMoodle --> AuthorizationGate).
     It is set to false by default, because otherwise accessing "/" by an already authorized user would not automatically redirect him to the auth page.
     */
    const [moodleSignInInProgress, setMoodleSignInInProgress] = useState(false)

    const signInWithCustomToken = async () => {
        setMoodleSignInInProgress(true)

        // Handling the special case that the user is already logged in with another username
        await firebaseService.auth().signOut()
        /*
        Set the LTI key we get from Moodle in the FirebaseAuthContext, so that we can use it
        throughout the application. This key is not important for the actual REG session, but
        it becomes necessary as soon as a communication with Moodle is required.
        IMPORTANT: The context's lti key (in FirebaseAuthContext) cannot be used in this function,
        as it is not updated immediately, but in successive components it is accessible.
         */
        setSignedInUserMoodleLtiKey(ltiKey)

        return firebaseService.auth().signInWithCustomToken(firebaseCustomToken)
            .then(async (res) => {

                // After successful sign-in, the created firebase user is enriched with the data from Moodle
                await res.user.updateEmail(moodleEmail)
                await res.user.updateProfile({displayName: moodleName})

                // Storing the Moodle return URL in the browser (will be used on the sign-in screen after sign-out)
                localStorage.setItem('moodleReturnUrl', moodleReturnUrl)

                // Pass ID token to the next then-handler
                return res.user.getIdToken()
            })
            .then(async (idToken) => {

                //Bootstrapping initial roles for the signed-in user
                return fetch('/reg-main/profile/initRole', {
                    method: 'POST',
                    headers: {'Content-Type': 'application/json'},
                    body: JSON.stringify({idToken: idToken})
                })
            })
            .then(async (data) => {
                // Throw an error including the textual status of the HTTP response code (no detailed message necessary)
                if (data.status !== 200) {
                    throw new Error((await data.json()).status)
                }

                /*
                  After successful login, let us refresh the id token.
                  IMPORTANT: This will trigger the onIdTokenChanged listener in the FirebaseAuthProvider.
                  IMPORTANT: The refresh is actually only necessary at the first login (as the roles are
                  initially set and are required to be refreshed after). All subsequent roles are immediately
                  retrieved after the login.
                   */
                return firebaseService.auth().currentUser.getIdToken(true)
            })
            .catch(async (error) => {
                enqueueSnackbar(
                    intl.formatMessage({
                        id: 'app.signIn.messages.moodleSignIn.login.error',
                        defaultMessage: 'Something went wrong while signing-in with your user: {error}'
                    }, {error: error.message}), {
                        variant: 'error',
                        preventDuplicate: true,
                        autoHideDuration: 5000
                    });
                clearUserData()
                await firebaseService.auth().signOut()

                // Redirection to the main login page in case of error
                history.push('/')
            })
            .finally(() => {
                setMoodleSignInInProgress(false)
            })
    }

    useEffect(() => {
        signInWithCustomToken()
    }, []);

    return {moodleSignInInProgress}
}
export default useMoodleSignIn;